import { useCallback, useEffect, useRef, useState } from 'react';
import { detectIOS, detectPortrait, SAFE_TOP_FLOOR_VAR } from '../../utils/ui/safeTopFloor';
import { cssMobileShellHeaderPaddingTop } from '../../ui/shellIdentityStyles';
import { IS_DEBUG_BUILD } from '../../utils/env/debugBuild';

/**
 * Developer viewport/safe-area diagnostic overlay — rendered ONLY behind
 * `?vpdebug=1` in the URL, and ONLY in a debug build (localhost or the dev
 * deployment — see `debugBuild.js`; production cannot enable it at all).
 * Never shown to a real user, so this file is
 * intentionally EXEMPT from the i18n-parity rule (CLAUDE.md's EN+ES
 * requirement is for user-facing strings; this is a dev-only readout).
 *
 * Raw px/rem values below are deliberate too — this is a raw-metric probe,
 * not app chrome, so it does not pull from the design-token system.
 */

const DEBUG_PARAM = 'vpdebug';
const DEBUG_VALUE = '1';

function isDebugEnabled() {
  if (!IS_DEBUG_BUILD) return false;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get(DEBUG_PARAM) === DEBUG_VALUE;
  } catch {
    return false;
  }
}

function round1(value) {
  return typeof value === 'number' ? Math.round(value * 10) / 10 : null;
}

// z-index above overlayLayers.appUpdateGate (12500, the app's highest tier —
// see src/ui/overlayLayers.js) so this dev readout is never hidden behind it.
const PANEL_Z_INDEX = 999999;

const panelStyle = {
  position: 'fixed',
  top: 4,
  left: 4,
  zIndex: PANEL_Z_INDEX,
  pointerEvents: 'none',
  maxWidth: '92vw',
  padding: '8px 10px',
  background: 'rgba(15, 23, 42, 0.86)',
  color: '#ffffff',
  fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
  fontSize: '11px',
  lineHeight: 1.5,
  borderRadius: '6px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

// Off-screen probes: laid out (not display:none) so `env()`/`*vh` lengths
// actually resolve, but invisible and inert.
const probeStyle = {
  position: 'absolute',
  top: '-9999px',
  left: '-9999px',
  width: '1px',
  visibility: 'hidden',
  pointerEvents: 'none',
};

export default function ViewportDebugOverlay() {
  const [enabled] = useState(isDebugEnabled);
  const [snap, setSnap] = useState(null);
  const envTopRef = useRef(null);
  const envBottomRef = useRef(null);
  const dvhRef = useRef(null);
  const lvhRef = useRef(null);
  const svhRef = useRef(null);
  const headerPadRef = useRef(null);

  const measure = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const standaloneMq = typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches;
    const standaloneLegacy = window.navigator && window.navigator.standalone === true;
    const mode = (standaloneMq || standaloneLegacy) ? 'standalone' : 'browser';

    const floorRaw = getComputedStyle(document.documentElement).getPropertyValue(SAFE_TOP_FLOOR_VAR);
    const vv = window.visualViewport;

    setSnap({
      mode,
      iOS: detectIOS(window.navigator),
      orientation: detectPortrait(window) ? 'portrait' : 'landscape',
      floor: floorRaw ? floorRaw.trim() : 'n/a',
      envTop: envTopRef.current ? envTopRef.current.offsetHeight : null,
      envBottom: envBottomRef.current ? envBottomRef.current.offsetHeight : null,
      innerH: window.innerHeight,
      screenH: window.screen ? window.screen.height : null,
      vvHeight: vv ? round1(vv.height) : null,
      vvOffsetTop: vv ? round1(vv.offsetTop) : null,
      dvh: dvhRef.current ? round1(dvhRef.current.getBoundingClientRect().height) : null,
      lvh: lvhRef.current ? round1(lvhRef.current.getBoundingClientRect().height) : null,
      svh: svhRef.current ? round1(svhRef.current.getBoundingClientRect().height) : null,
      // Resolved from a probe carrying the SAME expression every mobile shell
      // header uses, rather than querying a live header node — no role shell has
      // to be tagged, and it reads correctly on the login screen too.
      headerPad: headerPadRef.current
        ? getComputedStyle(headerPadRef.current).paddingTop
        : 'n/a',
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    measure();
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onChange);
    vv?.addEventListener('scroll', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      vv?.removeEventListener('resize', onChange);
      vv?.removeEventListener('scroll', onChange);
    };
  }, [enabled, measure]);

  if (!enabled) return null;

  return (
    <>
      <div ref={envTopRef} style={{ ...probeStyle, height: 'env(safe-area-inset-top, 0px)' }} />
      <div ref={envBottomRef} style={{ ...probeStyle, height: 'env(safe-area-inset-bottom, 0px)' }} />
      <div ref={dvhRef} style={{ ...probeStyle, height: '100dvh' }} />
      <div ref={lvhRef} style={{ ...probeStyle, height: '100lvh' }} />
      <div ref={svhRef} style={{ ...probeStyle, height: '100svh' }} />
      <div
        ref={headerPadRef}
        style={{ ...probeStyle, height: 0, paddingTop: cssMobileShellHeaderPaddingTop() }}
      />
      <div style={panelStyle}>
        {snap ? [
          `mode: ${snap.mode}`,
          `iOS: ${snap.iOS}`,
          `orientation: ${snap.orientation}`,
          `floor: ${snap.floor}`,
          `env-top: ${snap.envTop ?? 'n/a'}px`,
          `env-bottom: ${snap.envBottom ?? 'n/a'}px`,
          `innerH / screenH: ${snap.innerH} / ${snap.screenH ?? 'n/a'}`,
          `vv.height / vv.offsetTop: ${snap.vvHeight ?? 'n/a'} / ${snap.vvOffsetTop ?? 'n/a'}`,
          `dvh / lvh / svh: ${snap.dvh ?? 'n/a'} / ${snap.lvh ?? 'n/a'} / ${snap.svh ?? 'n/a'}`,
          `header-pad: ${snap.headerPad}`,
        ].join('\n') : 'measuring…'}
      </div>
    </>
  );
}
